import { useQuery } from "@tanstack/react-query";
import type { OlympicGame } from "@/data/olympics2026Schedule";
import { supabase } from "@/integrations/supabase/client";

export interface OlympicsData {
  openingCeremony: string;
  firstHockeyGame: string;
  broadcastInfo: {
    svtPlay: { name: string; url: string };
    tv4Play: { name: string; url: string };
    note: string;
  };
  womensSchedule: OlympicGame[];
  mensSchedule: OlympicGame[];
}

interface LiveGameResult {
  gameId: string;
  date: string;
  homeTeam: string;
  awayTeam: string;
  homeScore: number | null;
  awayScore: number | null;
  status: 'upcoming' | 'live' | 'finished';
  period: string | null;
  tournament: 'women' | 'men';
}

const fetchOlympicsSchedule = async (): Promise<OlympicsData> => {
  const response = await fetch('/data/olympics2026.json');
  if (!response.ok) {
    throw new Error('Failed to fetch Olympics schedule');
  }
  return response.json();
};

const fetchLiveResults = async (): Promise<LiveGameResult[]> => {
  try {
    const { data, error } = await supabase.functions.invoke('olympics-results');
    if (error) {
      console.warn('Failed to fetch live Olympics results:', error);
      return [];
    }
    return data?.results ?? [];
  } catch (err) {
    console.warn('Olympics live results fetch error:', err);
    return [];
  }
};

interface ScrapeResult {
  games: Array<{
    date: string;
    time: string;
    homeTeam: string;
    awayTeam: string;
    homeScore: number | null;
    awayScore: number | null;
    status: 'upcoming' | 'live' | 'finished';
    tournament: 'women' | 'men';
    stage: string;
  }>;
  scrapedAt: string | null;
  stale?: boolean;
}

const fetchScrapedData = async (): Promise<ScrapeResult> => {
  try {
    const { data, error } = await supabase.functions.invoke('olympics-scrape');
    if (error) {
      console.warn('Failed to fetch scraped Olympics data:', error);
      return { games: [], scrapedAt: null };
    }
    return data ?? { games: [], scrapedAt: null };
  } catch (err) {
    console.warn('Olympics scrape fetch error:', err);
    return { games: [], scrapedAt: null };
  }
};

// Flag emoji lookup for dynamic team resolution
const TEAM_FLAGS: Record<string, string> = {
  'United States': '🇺🇸', 'Canada': '🇨🇦', 'Sweden': '🇸🇪', 'Finland': '🇫🇮',
  'Czechia': '🇨🇿', 'Switzerland': '🇨🇭', 'Germany': '🇩🇪', 'Slovakia': '🇸🇰',
  'France': '🇫🇷', 'Latvia': '🇱🇻', 'Denmark': '🇩🇰', 'Italy': '🇮🇹',
  'Japan': '🇯🇵', 'Norway': '🇳🇴', 'Austria': '🇦🇹', 'Kazakhstan': '🇰🇿',
  'Hungary': '🇭🇺',
};

const PLACEHOLDER_TEAMS = ['QF Winner', 'SF Winner', 'SF Loser', 'QP Winner', 'TBD'];

function isPlaceholderTeam(name: string): boolean {
  return PLACEHOLDER_TEAMS.some(p => name.startsWith(p) || name === 'TBD');
}

/**
 * Match a live result to a schedule game by date + team names,
 * or by date + time + tournament for placeholder games
 */
function matchResult(game: OlympicGame, results: LiveGameResult[]): LiveGameResult | undefined {
  // Exact team match first
  const exact = results.find(r =>
    r.date === game.date &&
    r.tournament === game.tournament &&
    ((r.homeTeam === game.homeTeam && r.awayTeam === game.awayTeam) ||
     (r.homeTeam === game.awayTeam && r.awayTeam === game.homeTeam))
  );
  if (exact) return exact;

  // Positional match for placeholder teams (date + approximate time + tournament)
  if (isPlaceholderTeam(game.homeTeam) || isPlaceholderTeam(game.awayTeam)) {
    // Find results on the same date & tournament that aren't matched to any non-placeholder game
    const candidates = results.filter(r =>
      r.date === game.date && r.tournament === game.tournament
    );
    // If there's only one candidate for this time slot, use it
    if (candidates.length === 1) return candidates[0];
  }

  return undefined;
}

/**
 * Merge live results into the static schedule, including updating placeholder team names
 */
function mergeResults(schedule: OlympicGame[], results: LiveGameResult[]): OlympicGame[] {
  if (!results.length) return schedule;

  // Track which results have been consumed to avoid double-matching
  const usedResults = new Set<string>();

  return schedule.map(game => {
    const match = results.find(r => {
      if (usedResults.has(r.gameId)) return false;

      // Exact team match
      if (r.date === game.date && r.tournament === game.tournament &&
          ((r.homeTeam === game.homeTeam && r.awayTeam === game.awayTeam) ||
           (r.homeTeam === game.awayTeam && r.awayTeam === game.homeTeam))) {
        return true;
      }

      // Positional match for placeholders
      if ((isPlaceholderTeam(game.homeTeam) || isPlaceholderTeam(game.awayTeam)) &&
          r.date === game.date && r.tournament === game.tournament) {
        return true;
      }

      return false;
    });

    if (!match) return game;
    usedResults.add(match.gameId);

    const isSwapped = match.homeTeam === game.awayTeam && match.awayTeam === game.homeTeam;
    const homeScore = isSwapped ? match.awayScore : match.homeScore;
    const awayScore = isSwapped ? match.homeScore : match.awayScore;

    if (match.status === 'upcoming' && game.status === 'finished') {
      return game;
    }

    // Update team names if current ones are placeholders
    const resolvedHome = isPlaceholderTeam(game.homeTeam) ? match.homeTeam : game.homeTeam;
    const resolvedAway = isPlaceholderTeam(game.awayTeam) ? match.awayTeam : game.awayTeam;

    return {
      ...game,
      homeTeam: resolvedHome,
      awayTeam: resolvedAway,
      homeFlag: isPlaceholderTeam(game.homeTeam) ? (TEAM_FLAGS[resolvedHome] || game.homeFlag) : game.homeFlag,
      awayFlag: isPlaceholderTeam(game.awayTeam) ? (TEAM_FLAGS[resolvedAway] || game.awayFlag) : game.awayFlag,
      isSweden: resolvedHome === 'Sweden' || resolvedAway === 'Sweden',
      status: match.status,
      homeScore: homeScore ?? game.homeScore,
      awayScore: awayScore ?? game.awayScore,
      apiGameId: match.gameId ? parseInt(match.gameId, 10) : undefined,
      periodShort: match.status === 'live' ? (match.period ?? null) : null,
    };
  });
}

export const useOlympicsSchedule = () => {
  // Fetch static schedule
  const scheduleQuery = useQuery({
    queryKey: ['olympics-2026-schedule'],
    queryFn: fetchOlympicsSchedule,
    staleTime: 1000 * 60 * 60, // 1 hour
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
  });

  // Fetch live results (disabled - tournament ended)
  const liveQuery = useQuery({
    queryKey: ['olympics-2026-live-results'],
    queryFn: fetchLiveResults,
    staleTime: Infinity,
    enabled: false, // Tournament ended
    gcTime: 1000 * 60 * 10,
  });

  // Fetch scraped data (disabled - tournament ended)
  const scrapeQuery = useQuery({
    queryKey: ['olympics-2026-scrape'],
    queryFn: fetchScrapedData,
    staleTime: Infinity,
    enabled: false, // Tournament ended
    gcTime: 1000 * 60 * 30,
  });

  // Merge scraped games into schedule as a secondary source
  const applyScrapedUpdates = (schedule: OlympicGame[], scraped: ScrapeResult['games'], tournament: 'women' | 'men'): OlympicGame[] => {
    const tournamentGames = scraped.filter(g => g.tournament === tournament);
    if (!tournamentGames.length) return schedule;

    const usedScraped = new Set<number>();

    return schedule.map(game => {
      // Try exact team match first
      let matchIdx = tournamentGames.findIndex((s, idx) =>
        !usedScraped.has(idx) &&
        s.date === game.date &&
        s.homeTeam === game.homeTeam &&
        s.awayTeam === game.awayTeam
      );

      // Positional match for placeholders (date + stage)
      if (matchIdx === -1 && (isPlaceholderTeam(game.homeTeam) || isPlaceholderTeam(game.awayTeam))) {
        matchIdx = tournamentGames.findIndex((s, idx) =>
          !usedScraped.has(idx) &&
          s.date === game.date &&
          s.stage.toLowerCase().includes(game.stage.toLowerCase().split(' ')[0])
        );
      }

      if (matchIdx === -1) return game;
      usedScraped.add(matchIdx);
      const match = tournamentGames[matchIdx];

      // Resolve placeholder team names
      const resolvedHome = isPlaceholderTeam(game.homeTeam) && match.homeTeam ? match.homeTeam : game.homeTeam;
      const resolvedAway = isPlaceholderTeam(game.awayTeam) && match.awayTeam ? match.awayTeam : game.awayTeam;

      const updatedGame: OlympicGame = {
        ...game,
        homeTeam: resolvedHome,
        awayTeam: resolvedAway,
        homeFlag: isPlaceholderTeam(game.homeTeam) ? (TEAM_FLAGS[resolvedHome] || game.homeFlag) : game.homeFlag,
        awayFlag: isPlaceholderTeam(game.awayTeam) ? (TEAM_FLAGS[resolvedAway] || game.awayFlag) : game.awayFlag,
        isSweden: resolvedHome === 'Sweden' || resolvedAway === 'Sweden',
      };

      // Update scores/status if scraped data has more info
      if (match.status === 'finished' && game.status !== 'finished' && match.homeScore !== null) {
        updatedGame.status = match.status;
        updatedGame.homeScore = match.homeScore ?? game.homeScore;
        updatedGame.awayScore = match.awayScore ?? game.awayScore;
      } else if (match.status === 'live') {
        updatedGame.status = 'live';
        if (match.homeScore !== null) updatedGame.homeScore = match.homeScore;
        if (match.awayScore !== null) updatedGame.awayScore = match.awayScore;
      }

      return updatedGame;
    });
  };

  // Merge: static → scraped → live (live takes priority)
  const mergedData = scheduleQuery.data ? (() => {
    const scrapedGames = scrapeQuery.data?.games ?? [];
    
    // First apply scraped updates
    const womensWithScrape = applyScrapedUpdates(scheduleQuery.data.womensSchedule, scrapedGames, 'women');
    const mensWithScrape = applyScrapedUpdates(scheduleQuery.data.mensSchedule, scrapedGames, 'men');
    
    // Then apply live results (highest priority)
    return {
      ...scheduleQuery.data,
      womensSchedule: mergeResults(
        womensWithScrape,
        (liveQuery.data ?? []).filter(r => r.tournament === 'women')
      ),
      mensSchedule: mergeResults(
        mensWithScrape,
        (liveQuery.data ?? []).filter(r => r.tournament === 'men')
      ),
    };
  })() : undefined;

  // Determine latest update timestamp
  const lastUpdated = Math.max(
    liveQuery.dataUpdatedAt || 0,
    scrapeQuery.dataUpdatedAt || 0,
    scheduleQuery.dataUpdatedAt || 0,
  );

  return {
    data: mergedData,
    isLoading: scheduleQuery.isLoading,
    error: scheduleQuery.error,
    isLiveLoading: liveQuery.isLoading,
    lastLiveUpdate: liveQuery.dataUpdatedAt,
    lastUpdated: lastUpdated > 0 ? lastUpdated : null,
    scrapedAt: scrapeQuery.data?.scrapedAt ?? null,
    isScrapeStale: scrapeQuery.data?.stale ?? false,
  };
};

// Helper to convert string date to Date object for countdown
export const getOlympicsDates = (data: OlympicsData | undefined) => {
  if (!data) return { openingCeremony: new Date('2026-02-06T20:00:00+01:00') };
  return {
    openingCeremony: new Date(data.openingCeremony),
    firstHockeyGame: new Date(data.firstHockeyGame),
  };
};
